// Pythagorean Numerology — client-side calculations for preview

const PYTHAGOREAN = {
  A:1,B:2,C:3,D:4,E:5,F:6,G:7,H:8,I:9,
  J:1,K:2,L:3,M:4,N:5,O:6,P:7,Q:8,R:9,
  S:1,T:2,U:3,V:4,W:5,X:6,Y:7,Z:8,
};
const VOWELS = new Set(['A','E','I','O','U']);

// All repdigit master numbers up to 99 (used for name-based calculations)
const MASTER_NUMBERS = new Set([11, 22, 33, 44, 55, 66, 77, 88, 99]);
// Life Path traditionally only recognises 11, 22 and 33 as masters.
const LIFE_PATH_MASTERS = new Set([11, 22, 33]);

function reduceNum(n, masters = MASTER_NUMBERS) {
  while (n > 9 && !masters.has(n)) {
    n = String(n).split('').reduce((s,d) => s + parseInt(d,10), 0);
  }
  return n;
}

function fullReduce(n) {
  while (n > 9) {
    n = String(n).split('').reduce((s,d) => s + parseInt(d,10), 0);
  }
  return n;
}

// Format for display: masters show as "33/6", others as "5"
export function fmtNum(n) {
  if (n > 9 && MASTER_NUMBERS.has(n)) return `${n}/${fullReduce(n)}`;
  return String(n);
}

export function calcLifePath(day, month, year) {
  // Sum all digits of the full DOB then reduce.
  // Life Path traditionally recognises only 11, 22, 33 as masters —
  // 44+ are reduced to a single digit when they appear here.
  const dobStr = `${String(month).padStart(2,'0')}${String(day).padStart(2,'0')}${String(year).padStart(4,'0')}`;
  const sum = dobStr.split('').reduce((s,c) => s + parseInt(c,10), 0);
  return reduceNum(sum, LIFE_PATH_MASTERS);
}

export function calcExpression(name) {
  const vals = name.toUpperCase().split('').filter(c => PYTHAGOREAN[c]).map(c => PYTHAGOREAN[c]);
  return vals.length ? reduceNum(vals.reduce((s,v)=>s+v,0)) : 1;
}

export function calcSoulUrge(name) {
  const vals = name.toUpperCase().split('').filter(c => PYTHAGOREAN[c] && VOWELS.has(c)).map(c => PYTHAGOREAN[c]);
  return vals.length ? reduceNum(vals.reduce((s,v)=>s+v,0)) : 1;
}

export function calcPersonality(name) {
  const vals = name.toUpperCase().split('').filter(c => PYTHAGOREAN[c] && !VOWELS.has(c)).map(c => PYTHAGOREAN[c]);
  return vals.length ? reduceNum(vals.reduce((s,v)=>s+v,0)) : 1;
}

export function calcBirthday(day) {
  return reduceNum(day);
}

export function calcMaturity(lifePath, expression) {
  return reduceNum(lifePath + expression);
}

export function calcPersonalYear(day, month) {
  const year = new Date().getFullYear();
  const y = reduceNum(String(year).split('').reduce((s,c)=>s+parseInt(c,10),0));
  return reduceNum(month + day + y);
}

const STAR_SIGNS = [
  { symbol: '♑', name: 'Capricorn',   check: (m,d) => m===12&&d>=22 || m===1&&d<=19 },
  { symbol: '♒', name: 'Aquarius',    check: (m,d) => (m===1&&d>=20) || (m===2&&d<=18) },
  { symbol: '♓', name: 'Pisces',      check: (m,d) => (m===2&&d>=19) || (m===3&&d<=20) },
  { symbol: '♈', name: 'Aries',       check: (m,d) => (m===3&&d>=21) || (m===4&&d<=19) },
  { symbol: '♉', name: 'Taurus',      check: (m,d) => (m===4&&d>=20) || (m===5&&d<=20) },
  { symbol: '♊', name: 'Gemini',      check: (m,d) => (m===5&&d>=21) || (m===6&&d<=20) },
  { symbol: '♋', name: 'Cancer',      check: (m,d) => (m===6&&d>=21) || (m===7&&d<=22) },
  { symbol: '♌', name: 'Leo',         check: (m,d) => (m===7&&d>=23) || (m===8&&d<=22) },
  { symbol: '♍', name: 'Virgo',       check: (m,d) => (m===8&&d>=23) || (m===9&&d<=22) },
  { symbol: '♎', name: 'Libra',       check: (m,d) => (m===9&&d>=23) || (m===10&&d<=22) },
  { symbol: '♏', name: 'Scorpio',     check: (m,d) => (m===10&&d>=23) || (m===11&&d<=21) },
  { symbol: '♐', name: 'Sagittarius', check: (m,d) => (m===11&&d>=22) || (m===12&&d<=21) },
];

export function getStarSign(month, day) {
  const sign = STAR_SIGNS.find(s => s.check(month, day));
  return sign ? sign : { symbol: '♑', name: 'Capricorn' };
}

export function calculateAll(name, day, month, year) {
  const lifePath = calcLifePath(day, month, year);
  const expression = calcExpression(name);
  const soulUrge = calcSoulUrge(name);
  const personality = calcPersonality(name);
  const birthday = calcBirthday(day);
  const maturity = calcMaturity(lifePath, expression);
  const personalYear = calcPersonalYear(day, month);
  const sign = getStarSign(month, day);
  return {
    life_path: lifePath,
    expression,
    soul_urge: soulUrge,
    personality,
    birthday,
    maturity,
    personal_year: personalYear,
    star_sign: `${sign.symbol} ${sign.name}`,
    // Display-ready strings (masters shown as "33/6")
    life_path_display: fmtNum(lifePath),
    expression_display: fmtNum(expression),
    soul_urge_display: fmtNum(soulUrge),
    personality_display: fmtNum(personality),
    birthday_display: fmtNum(birthday),
    maturity_display: fmtNum(maturity),
    personal_year_display: fmtNum(personalYear),
  };
}
